import { useEffect } from 'preact/hooks';

import { useSelector } from '@/redux/helper';

import useOpenSpeedyFlow from './useOpenSpeedyFlow';

/**
 * Ask a signed-in visitor to confirm their address, without taking the page away from them.
 *
 * This replaced a redirect to the full-page verification route. That redirect was written before
 * anything could verify in place, and it had become the wrong shape twice over: it unmounted the
 * page a running flow was deliberately keeping the visitor on — throwing away a product they may
 * already have chosen — and for everyone else it was the sort of dead-end bounce this whole feature
 * exists to remove.
 *
 * Nothing is enforced here, and nothing was before either: no endpoint refuses an unverified user,
 * so the redirect was only ever a nudge. This is the same nudge without the bounce — dismissable,
 * and offered again next time they arrive.
 *
 * Works the condition out itself rather than taking it, because it is the same condition wherever
 * it is asked and the callers had drifted: one checked for a session, the other relied on `role`
 * never being `user` without one. That happens to hold — the user slice is not persisted, so a
 * signed-out visitor's role is `null` or `guest` — but it is a property of another module, and a
 * gate that asks a guest to confirm an address they have not given is not worth resting on it.
 *
 * `role` is checked because only ordinary accounts are asked: admins, hosts and partners are not.
 */
const useVerifyEmailGate = () => {
  const openSpeedyFlow = useOpenSpeedyFlow();
  const logged = useSelector((state) => state.user.logged);
  const role = useSelector((state) => state.user.role);
  const isRealEmail = useSelector((state) => state.user.isRealEmail);

  useEffect(() => {
    if (!logged || role !== 'user' || isRealEmail) {
      return;
    }
    openSpeedyFlow({ kind: 'verified-email', entryPoint: 'unverified-email' });
  }, [logged, role, isRealEmail, openSpeedyFlow]);
};

export default useVerifyEmailGate;
